import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Lightbox = ({ images, index, onClose, onPrev, onNext, categoryLabel }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const image = index !== null ? images[index] : null;

  return (
    <AnimatePresence>
      {image && (
        <motion.div
          data-testid="gallery-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1F1F1F]/95 p-4 backdrop-blur-sm sm:p-10"
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-[#C62828]"
          >
            <X size={22} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            aria-label="Previous image"
            className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-[#C62828] sm:left-6"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            aria-label="Next image"
            className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-[#C62828] sm:right-6"
          >
            <ChevronRight size={24} />
          </button>
          <motion.figure
            key={image.src}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-5xl"
          >
            <img
              src={image.src}
              alt={image.title}
              className="max-h-[76vh] w-auto max-w-full rounded-xl object-contain shadow-2xl"
            />
            <figcaption data-testid="lightbox-caption" className="mt-4 text-center">
              <span className="text-sm font-semibold text-white">{image.title}</span>
              {categoryLabel && (
                <span className="mt-1 block text-xs uppercase tracking-wider text-[#E05252]">
                  {categoryLabel}
                </span>
              )}
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
